/**
 * Resolve public runtime configuration from Expo config first, then process env.
 * This keeps release bundles working even when Babel @env injection is absent.
 */
import Constants from 'expo-constants';

type ExtraConfig = Record<string, unknown> | null | undefined;

function readExpoExtraValue(key: string): string | undefined {
  const manifestExtra = (Constants.manifest as { extra?: ExtraConfig } | null | undefined)?.extra;
  const expoConfigExtra = Constants.expoConfig?.extra as ExtraConfig;
  const manifest2Extra = (
    Constants as typeof Constants & {
      manifest2?: {
        extra?: {
          expoClient?: {
            extra?: ExtraConfig;
          };
          [key: string]: unknown;
        };
      };
    }
  ).manifest2?.extra;

  const candidates = [
    expoConfigExtra?.[key],
    manifestExtra?.[key],
    manifest2Extra?.[key],
    manifest2Extra?.expoClient?.extra?.[key],
  ];

  for (const candidate of candidates) {
    const normalized = normalizeEnvValue(candidate);
    if (normalized) {
      return normalized;
    }
  }

  return undefined;
}

function normalizeEnvValue(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim().replace(/^['"]|['"]$/g, '');
  if (!trimmed || trimmed === 'undefined' || trimmed === 'null') {
    return undefined;
  }

  return trimmed;
}

export function getEnvVar(value: string | undefined, key: string, required = true): string {
  const resolvedValue = normalizeEnvValue(value) ?? readExpoExtraValue(key);

  if (required && !resolvedValue) {
    console.error(`Missing required environment variable: ${key}`);
    // Return a safe fallback for known keys instead of crashing on startup
    const fallbacks: Record<string, string> = {
      EXPO_PUBLIC_SUPABASE_URL: 'https://gghwxnwzjpmqffuyuiei.supabase.co',
    };
    if (fallbacks[key]) {
      console.warn(`Using hardcoded fallback for ${key}`);
      return fallbacks[key];
    }
    throw new Error(`Missing required environment variable: ${key}`);
  }

  return resolvedValue ?? '';
}

// Export common environment variables for easy access.
export const SUPABASE_URL = getEnvVar(process.env.EXPO_PUBLIC_SUPABASE_URL, 'EXPO_PUBLIC_SUPABASE_URL');
export const SUPABASE_ANON_KEY = getEnvVar(process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY, 'EXPO_PUBLIC_SUPABASE_ANON_KEY');
